import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Bell, CalendarClock, MessageSquare, CheckCircle2, XCircle, Send, Car, BadgeDollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { organizationsAPI, vehicleLeadsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { OfferDepositCard, isOfferRequest } from '../components/shared/OfferDepositCard';
import { AutoSalesAlerts } from './commandCenter/AutoSalesAlerts';

export default function VehicleLeads() {
    const { t } = useTranslation();
    const { user, isSuperAdmin } = useAuth();
    const superAdmin = typeof isSuperAdmin === 'function' ? isSuperAdmin() : user?.role === 'super_admin';

    const [orgs, setOrgs] = useState([]);
    const [org, setOrg] = useState(user?.organization_id || '');
    const [drives, setDrives] = useState([]);
    const [sales, setSales] = useState([]);
    const [thread, setThread] = useState(null); // active sales request
    const [msgs, setMsgs] = useState([]);
    const [reply, setReply] = useState('');

    useEffect(() => {
        organizationsAPI.list().then((r) => {
            const auto = (r.data || []).filter((o) => o.vertical === 'automotive');
            setOrgs(auto);
            if (!org && auto.length) setOrg(auto[0].id);
        }).catch(() => {});
    }, []); // eslint-disable-line

    const load = useCallback(async () => {
        if (!org) return;
        try {
            const [d, s] = await Promise.all([vehicleLeadsAPI.testDrives(org), vehicleLeadsAPI.salesRequests(org)]);
            setDrives(d.data || []); setSales(s.data || []);
        } catch { /* noop */ }
    }, [org]);
    useEffect(() => { load(); }, [load]);

    const act = async (fn, id) => { try { await fn(id); toast.success(t('leads.updated', 'Updated')); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } };

    const openThread = async (q) => {
        setThread(q); setReply('');
        try { const r = await vehicleLeadsAPI.thread(q.id); setMsgs(r.data?.messages || []); } catch { setMsgs([]); }
    };
    const sendReply = async () => {
        if (!reply.trim()) return;
        try { await vehicleLeadsAPI.reply(thread.id, { body: reply }); setReply(''); const r = await vehicleLeadsAPI.thread(thread.id); setMsgs(r.data?.messages || []); load(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    const badge = (s) => ({ requested: 'bg-amber-500/90', booked: 'bg-emerald-500/90', declined: 'bg-red-500/90', open: 'bg-blue-500/90' }[s] || 'bg-neutral-500');

    // An offer / deposit is the same record as an inquiry, tagged by request_type.
    const offers = sales.filter(isOfferRequest);
    const inquiries = sales.filter((s) => !isOfferRequest(s));

    return (
        <div className="space-y-6 pb-16" data-testid="vehicle-leads-page">
            <div className="flex items-center justify-between gap-4">
                <h1 className="text-2xl font-bold flex items-center gap-2 text-slate-900 dark:text-white"><Car className="w-6 h-6 text-amber-500" />{t('leads.title', 'Leads & Enquiries')}</h1>
                {superAdmin && orgs.length > 1 && (
                    <Select value={org} onValueChange={setOrg}>
                        <SelectTrigger className="w-56" data-testid="leads-org-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                    </Select>
                )}
            </div>

            <Tabs defaultValue="alerts">
                <TabsList className="flex flex-wrap h-auto gap-1">
                    <TabsTrigger value="alerts" data-testid="leads-tab-alerts"><Bell className="w-4 h-4 mr-1.5" />{t('autoAlerts.tabTitle', 'Sales Alerts')}</TabsTrigger>
                    <TabsTrigger value="testdrives" data-testid="leads-tab-testdrives"><CalendarClock className="w-4 h-4 mr-1.5" />{t('leads.testDrives', 'Test Drives')} ({drives.length})</TabsTrigger>
                    <TabsTrigger value="sales" data-testid="leads-tab-sales"><MessageSquare className="w-4 h-4 mr-1.5" />{t('leads.salesInquiries', 'Sales Inquiries')} ({inquiries.length})</TabsTrigger>
                    <TabsTrigger value="offers" data-testid="leads-tab-offers"><BadgeDollarSign className="w-4 h-4 mr-1.5" />{t('offerDeposit.tabTitle', 'Offers & Deposits')} ({offers.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="alerts" className="mt-4" data-testid="leads-alerts">
                    <AutoSalesAlerts organizationId={org} scope="salesperson"
                        onOpenConversation={(rid) => { const q = sales.find((s) => s.id === rid); if (q) openThread(q); }} />
                </TabsContent>

                <TabsContent value="testdrives" className="space-y-3 mt-4">
                    {drives.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="leads-td-empty">{t('leads.noTestDrives', 'No test drive requests yet.')}</p> :
                        drives.map((d) => (
                            <div key={d.id} className="rounded-xl border border-border p-4 flex flex-wrap items-center gap-3" data-testid={`leads-td-${d.stock_number}-${d.id.slice(0, 6)}`}>
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-slate-900 dark:text-white">{d.year} {d.make} {d.model} <span className="text-muted-foreground font-normal">#{d.stock_number}</span></p>
                                    <p className="text-sm text-muted-foreground">{d.customer.first_name} {d.customer.last_name} · {d.customer.email}{d.customer.phone ? ` · ${d.customer.phone}` : ''}</p>
                                    <p className="text-sm">{d.requested_date} · {d.requested_time}{d.message ? ` — ${d.message}` : ''}</p>
                                </div>
                                <span className={`text-[11px] px-2 py-0.5 rounded-full text-white ${badge(d.status)}`} data-testid={`leads-td-status-${d.id.slice(0, 6)}`}>{t(`leads.status.${d.status}`, d.status)}</span>
                                {d.status === 'requested' && (
                                    <div className="flex gap-2">
                                        <Button size="sm" onClick={() => act(vehicleLeadsAPI.confirmTestDrive, d.id)} data-testid={`leads-td-confirm-${d.id.slice(0, 6)}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />{t('leads.confirm', 'Confirm Booking')}</Button>
                                        <Button size="sm" variant="outline" onClick={() => act(vehicleLeadsAPI.declineTestDrive, d.id)} data-testid={`leads-td-decline-${d.id.slice(0, 6)}`}><XCircle className="w-3.5 h-3.5 mr-1" />{t('leads.decline', 'Decline')}</Button>
                                    </div>
                                )}
                            </div>
                        ))}
                </TabsContent>

                <TabsContent value="offers" className="space-y-3 mt-4" data-testid="leads-offers">
                    {offers.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="leads-offers-empty">{t('offerDeposit.none', 'No offers or deposits yet.')}</p> :
                        offers.map((q) => (
                            <OfferDepositCard key={q.id} req={q} onDecided={load} onOpenThread={openThread} />
                        ))}
                </TabsContent>

                <TabsContent value="sales" className="space-y-3 mt-4">
                    {inquiries.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="leads-sales-empty">{t('leads.noSales', 'No sales inquiries yet.')}</p> :
                        inquiries.map((q) => (
                            <button key={q.id} onClick={() => openThread(q)} className="w-full text-left rounded-xl border border-border p-4 hover:border-amber-500 transition-colors" data-testid={`leads-sales-${q.stock_number}`}>
                                <div className="flex items-center justify-between gap-2">
                                    <p className="font-semibold text-slate-900 dark:text-white">{q.year} {q.make} {q.model} <span className="text-muted-foreground font-normal">#{q.stock_number}</span></p>
                                    <span className={`text-[11px] px-2 py-0.5 rounded-full text-white ${badge(q.status)}`}>{q.status}</span>
                                </div>
                                <p className="text-sm text-muted-foreground">{q.customer.name}{q.customer.email ? ` · ${q.customer.email}` : ''}</p>
                                {q.last_message && <p className="text-sm mt-1 truncate">{q.last_message.sender_role === 'sales' ? '↩ ' : ''}{q.last_message.body}</p>}
                                <p className={`text-[11px] mt-1.5 font-medium ${q.assigned_staff_id ? (q.assigned_staff_id === user?.id ? 'text-emerald-600' : 'text-amber-600') : 'text-blue-600'}`}
                                    data-testid={`leads-claim-${q.stock_number}`}>
                                    {q.assigned_staff_id
                                        ? (q.assigned_staff_id === user?.id
                                            ? t('broadcast.claimMine', 'You own this conversation')
                                            : t('broadcast.claimedBy', 'Claimed by {{name}}', { name: q.assigned_staff_name }))
                                        : t('broadcast.unclaimed', 'Unclaimed — first to reply owns it')}
                                </p>
                            </button>
                        ))}
                </TabsContent>
            </Tabs>

            <Dialog open={!!thread} onOpenChange={(o) => !o && setThread(null)}>
                <DialogContent className="sm:max-w-md" data-testid="leads-thread-modal">
                    <DialogHeader>
                        <DialogTitle>{thread && `${thread.year} ${thread.make} ${thread.model} · #${thread.stock_number}`}</DialogTitle>
                    </DialogHeader>
                    <div className="max-h-72 overflow-y-auto space-y-2" data-testid="leads-thread-messages">
                        {msgs.map((m) => (
                            <div key={m.id} className={`p-2.5 rounded-lg text-sm ${m.sender_role === 'sales' ? 'bg-amber-500/10 ml-8' : 'bg-muted mr-8'}`}>
                                <p className="text-[11px] text-muted-foreground mb-0.5 flex items-center gap-1.5">
                                    <span>{m.sender_name}</span>
                                    {m.channel === 'sms' && <span className="px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-500 text-[10px] font-medium" data-testid="leads-msg-sms">SMS{m.sms_status ? ` · ${m.sms_status}` : ''}</span>}
                                </p>
                                <p>{m.body}</p>
                                {m.photo_url && <img src={m.photo_url} alt="" className="mt-1.5 rounded-md max-h-40" />}
                            </div>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder={t('leads.replyPlaceholder', 'Type a reply…')} onKeyDown={(e) => e.key === 'Enter' && sendReply()} data-testid="leads-reply-input" />
                        <Button onClick={sendReply} data-testid="leads-reply-send"><Send className="w-4 h-4" /></Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
